/**
 * RouteErrorBoundary -- Route-Level Error Boundary
 *
 * Isolates errors within route content so that sidebar and header
 * remain functional. Logs the error to the console (so future
 * "every page shows this" reports are actually diagnosable), shows
 * an alert fallback, and offers two recovery paths:
 *
 *   - Try again: re-renders the same children. Useful when the
 *     underlying cause was transient (e.g. a one-shot fetch race,
 *     or stale in-memory state that a re-render with fresh data
 *     can recover from).
 *   - Reload page: full window.location.reload(). Useful when the
 *     cause is wedged client state that re-rendering won't clear —
 *     e.g. a stale Zustand store pointing at an entity that no
 *     longer exists, where every render re-trips the same selector.
 *
 * Note: the most common cause this boundary used to catch — a stale
 * lazy-loaded route chunk after a deploy — is now auto-handled by
 * lazyWithRetry (see src/lib/lazyWithRetry.ts), so reaching the
 * fallback UI today usually means a real render bug worth diagnosing.
 * Auth-expiry isn't a typical cause either: apiClient's 401 handler
 * logs the user out and redirects to /login before the boundary sees
 * anything.
 *
 * In dev (import.meta.env.DEV), the error message and component stack
 * are collapsed under a <details> for easy copy/paste while debugging.
 */

import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportClientError } from '../lib/telemetry';

interface RouteErrorBoundaryProps {
  children: ReactNode;
  /**
   * Changing this value resets the boundary's error state. AdminLayout
   * passes location.pathname so a sidebar nav from a broken route to a
   * fresh one re-tries the render instead of showing the fallback
   * indefinitely. Without this, error boundaries are sticky to their
   * tree position — they don't notice route changes on their own.
   */
  resetKey?: string;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

export class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<RouteErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log to the browser console so production reports of "every page
    // shows this error" are diagnosable from devtools. Without this the
    // boundary swallowed everything and the user had no recourse.
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary] caught render error', error, errorInfo);
    // First-party crash reporting (NFR-F-OBS-001): also ship it to the in-house
    // telemetry sink so the crash is diagnosable beyond this one browser's
    // (ephemeral) console. reportClientError never throws.
    reportClientError(error, errorInfo, 'route');
    this.setState({ componentStack: errorInfo.componentStack ?? null });
  }

  componentDidUpdate(prevProps: RouteErrorBoundaryProps): void {
    // Reset on route change so users can navigate away from a broken
    // page via the sidebar without a hard reload. resetKey is the
    // current pathname; when it changes, the previous tripped state is
    // no longer relevant — the new route gets a fresh render attempt.
    if (
      this.state.hasError &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ hasError: false, error: null, componentStack: null });
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, componentStack: null });
  };

  handleReload = (): void => {
    // Wedged client state (stale Zustand store pointing at a deleted
    // entity, a cached query keyed on a stale version_id, etc.) can't
    // be cleared by re-rendering the same tree. A hard reload re-runs
    // the auth check and re-fetches everything from scratch.
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const isDev = typeof import.meta !== 'undefined' && (import.meta as { env?: { DEV?: boolean } })?.env?.DEV;
      const message = this.state.error?.message;
      return (
        <div role="alert" className="p-8 text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-sm text-gray-600 mb-2">
            This section ran into an unexpected error. Try again first — transient issues usually clear on retry.
            If the problem persists, reload the page or report it to your administrator.
          </p>
          {isDev && message && (
            <details className="mb-4 max-w-2xl mx-auto text-left">
              <summary className="text-xs text-brand-steel cursor-pointer">Error details (dev only)</summary>
              <pre className="mt-2 p-3 bg-gray-100 rounded text-xs text-brand-error overflow-x-auto whitespace-pre-wrap">
{message}
{this.state.componentStack ?? ''}
              </pre>
            </details>
          )}
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={this.handleRetry}
              className="px-4 py-2 border border-brand-steel/50 bg-white text-brand-steel rounded-lg text-sm font-medium hover:bg-brand-panel"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="px-4 py-2 bg-brand-crimson text-white rounded-lg text-sm font-medium hover:bg-brand-crimson/90"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
